import { Node, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer } from "@tiptap/react";
import { TableNodeView } from "@/components/editor/nodeviews/TableNodeView";
import { readBorders, type TableBorders } from "@/lib/tables/borders";

export const TableBlock = Node.create({
  name: "tableBlock",
  group: "block",
  atom: true,

  addAttributes() {
    return {
      blockId: { default: null },
      headers: { default: ["Column 1", "Column 2", "Column 3"] },
      rows: { default: [["", "", ""], ["", "", ""]] },
      caption: { default: "" },
      label: { default: "" },
      // Per-column alignment ("l" | "c" | "r" | "p" | "m" | "b") and
      // optional matching width — drives the LaTeX `tabular{l c p{8cm}}`
      // spec on the API side. Empty arrays = all columns left-aligned.
      columnAlign: { default: [] as string[] },
      columnWidth: { default: [] as string[] },
      // The table's meaning (2g), per column: a unit the header carries and
      // which value is best (bold). Null = none; absent on tables without them.
      columnUnit: { default: null as (string | null)[] | null },
      columnBest: { default: null as (string | null)[] | null },
      // The rules preset ("booktabs" | "grid" | "none") and whether the first
      // row is a header. Null = not set (the table's defaults: booktabs, with a
      // header). They were not attributes, so a table that had them lost them
      // on its next save wherever the block's carried content did not come
      // along (inserted from the store, pasted), and a header-less table came
      // back with a header (6 Oct 2026).
      borders: {
        default: null as TableBorders | null,
        parseHTML: (el: HTMLElement) => readBorders(el.getAttribute("data-borders")),
        renderHTML: (attrs: Record<string, unknown>) => (attrs.borders ? { "data-borders": attrs.borders } : {}),
      },
      hasHeader: {
        default: null as boolean | null,
        parseHTML: (el: HTMLElement) => {
          const v = el.getAttribute("data-has-header");
          return v === "true" ? true : v === "false" ? false : null;
        },
        renderHTML: (attrs: Record<string, unknown>) =>
          typeof attrs.hasHeader === "boolean" ? { "data-has-header": String(attrs.hasHeader) } : {},
      },
      // The rest of the table's meaning (2g, table-grid/tableLayout.ts): group
      // headers above the header row, and the body rows that start a row group.
      // Null = none; absent on tables without them. Notes live on their cells.
      headerGroups: { default: null as { label: string; start: number; span: number }[] | null },
      rowGroups: { default: null as number[] | null },
      // Short caption for the List of Tables — emits
      // \caption[short]{long} when set together with `caption`.
      shortCaption: { default: "" },
      // Full width in two columns: "page" spans both (table*), null is one column (2 Nov 2026).
      span: { default: null as string | null },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="table-block"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ["div", mergeAttributes(HTMLAttributes, { "data-type": "table-block" })];
  },

  addNodeView() {
    return ReactNodeViewRenderer(TableNodeView);
  },
});
